<template>
  <div class="modern-filters-container">
    <!-- Compact Single Row Layout -->
    <div class="filters-compact-row">
      <!-- Search Filter Group -->
      <div class="filter-group search-group">
        <label class="filter-label">{{ $t('knowledge_base_search_label') }}</label>
        <div class="search-input-wrapper">
          <DsInput
            v-model="searchQuery"
            icon="search"
            @input="onSearchChange"
            :placeholder="$t('knowledge_base_search_placeholder')"
            is-simplified
            class="compact-search-input"
          />
        </div>
      </div>

      <!-- Category Filter Group -->
      <div class="filter-group">
        <label class="filter-label">{{ $t('knowledge_base_category_label') }}</label>
        <div class="relative">
          <button
            @click="showCategoryDropdown = !showCategoryDropdown"
            class="compact-dropdown"
          >
            {{ selectedCategoryText }}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="ml-2">
              <path d="M6 9l6 6 6-6"/>
            </svg>
          </button>

          <!-- Category Dropdown -->
          <div
            v-if="showCategoryDropdown"
            class="absolute right-0 mt-2 w-64 bg-white border border-gray-300 rounded-md shadow-lg z-50"
          >
            <div class="py-1 max-h-64 overflow-y-auto">
              <!-- All Categories Option -->
              <button
                @click="selectCategory(null)"
                :class="[
                'w-full text-left px-4 py-2 text-sm hover:bg-gray-100',
                selectedCategoryId === null ? 'bg-primary-lightest text-primary font-medium' : 'text-gray-900'
              ]"
              >
                {{ $t('knowledge_base_all_categories') }}
              </button>

              <!-- Uncategorized Option -->
              <button
                @click="selectCategory('uncategorized')"
                :class="[
                'w-full text-left px-4 py-2 text-sm hover:bg-gray-100',
                selectedCategoryId === 'uncategorized' ? 'bg-primary-lightest text-primary font-medium' : 'text-gray-900'
              ]"
              >
                {{ $t('knowledge_base_uncategorized') }}
              </button>

              <!-- Separator -->
              <div class="border-t border-gray-200 my-1"></div>

              <!-- Category Options -->
              <button
                v-for="category in categories"
                :key="category.id"
                @click="selectCategory(category.id)"
                :class="[
                'w-full text-left px-4 py-2 text-sm hover:bg-gray-100',
                selectedCategoryId === category.id ? 'bg-primary-lightest text-primary font-medium' : 'text-gray-900'
              ]"
              >
                {{ category.name }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Filter Tags Group -->
      <div class="filter-group" v-if="$store.getters.isLoggedIn">
        <label class="filter-label">{{ $t('knowledge_base_show_label') }}</label>
        <div class="compact-tags">
          <button
            @click="toggleBookmarkedFilter"
            :class="[
              'compact-filter-tag',
              { 'enabled': showBookmarkedOnly }
            ]"
          >
            {{ $t('knowledge_base_bookmarked_posts') }}
          </button>
        </div>
      </div>

      <!-- Actions Group -->
      <div class="filter-actions" v-if="hasActiveFilters">
        <button 
          @click="clearFilters" 
          class="clear-filters-btn" 
          :title="$t('knowledge_base_clear_filters_tooltip')"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Active Filters Section -->
    <div class="active-filters-section" v-if="hasActiveFilters">
      <div class="active-filters-label">{{ $t('knowledge_base_active_filters') }}</div>
      <div class="active-filters-tags">
        <!-- Search Filter Tag -->
        <span
          v-if="searchQuery"
          class="active-filter-tag"
          @click="clearSearch"
        >
          {{ $t('knowledge_base_search_filter_prefix') }} "{{ searchQuery }}"
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </span>

        <!-- Category Filter Tag -->
        <span
          v-if="selectedCategoryId !== null"
          class="active-filter-tag"
          @click="clearCategory"
        >
          {{ $t('knowledge_base_category_filter_prefix') }} {{ selectedCategoryText }}
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </span>

        <!-- Bookmarked Filter Tag -->
        <span
          v-if="showBookmarkedOnly"
          class="active-filter-tag"
          @click="clearBookmarkedFilter"
        >
          {{ $t('knowledge_base_bookmarked_posts') }}
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
  import Icon from '@/components/Icon/Icon.vue'
  import DsInput from '@/components/Form/DsInput.vue'

  export default {
    name: 'KnowledgeBaseFilters',
    components: {
      DsInput,
      Icon
    },
    props: {
      categories: {
        type: Array,
        default: () => []
      }
    },
    emits: ['search-changed', 'category-changed', 'bookmarked-filter-changed', 'filters-cleared'],
    data () {
      return {
        searchQuery: '',
        selectedCategoryId: null,
        showBookmarkedOnly: false,
        showCategoryDropdown: false,
        searchDebounceTimer: null
      }
    },
    computed: {
      selectedCategoryText () {
        if (this.selectedCategoryId === null) {
          return this.$t('knowledge_base_all_categories')
        }
        if (this.selectedCategoryId === 'uncategorized') {
          return this.$t('knowledge_base_uncategorized')
        }
        const category = this.categories.find(c => c.id === this.selectedCategoryId)
        return category ? category.name : this.$t('knowledge_base_all_categories')
      },
      hasActiveFilters () {
        return this.searchQuery.trim() !== '' || this.selectedCategoryId !== null || this.showBookmarkedOnly
      }
    },
    mounted () {
      // Close dropdown when clicking outside
      document.addEventListener('click', this.handleClickOutside)
    },
    beforeUnmount () {
      document.removeEventListener('click', this.handleClickOutside)
      clearTimeout(this.searchDebounceTimer)
    },
    methods: {
      onSearchChange () {
        // Debounce search to avoid too many API calls
        clearTimeout(this.searchDebounceTimer)
        this.searchDebounceTimer = setTimeout(() => {
          this.$emit('search-changed', this.searchQuery.trim())
        }, 300)
      },
      selectCategory (categoryId) {
        this.selectedCategoryId = categoryId
        this.showCategoryDropdown = false
        this.$emit('category-changed', categoryId)
      },
      clearSearch () {
        this.searchQuery = ''
        this.$emit('search-changed', '')
      },
      clearCategory () {
        this.selectedCategoryId = null
        this.$emit('category-changed', null)
      },
      onBookmarkedFilterChange () {
        this.$emit('bookmarked-filter-changed', this.showBookmarkedOnly)
      },
      toggleBookmarkedFilter () {
        this.showBookmarkedOnly = !this.showBookmarkedOnly
        this.$emit('bookmarked-filter-changed', this.showBookmarkedOnly)
      },
      clearBookmarkedFilter () {
        this.showBookmarkedOnly = false
        this.$emit('bookmarked-filter-changed', false)
      },
      clearFilters () {
        this.searchQuery = ''
        this.selectedCategoryId = null
        this.showBookmarkedOnly = false
        this.$emit('filters-cleared')
      },
      handleClickOutside (event) {
        if (!this.$el.contains(event.target)) {
          this.showCategoryDropdown = false
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  .modern-filters-container {
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    padding: 0.75rem;
    margin: 1rem 0;
    border: 1px solid #e5e7eb;

    .filters-compact-row {
      display: flex;
      align-items: center;
      gap: 1rem;
      flex-wrap: wrap;

      .filter-group {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        min-width: 120px;

        &.search-group {
          flex: 1;
          min-width: 200px;

          .search-input-wrapper {
            flex: 1;
          }
        }

        .filter-label {
          font-weight: 500;
          font-size: 0.75rem;
          color: #6b7280;
          white-space: nowrap;
          text-transform: uppercase;
          letter-spacing: 0.025em;
        }

        .compact-dropdown {
          display: inline-flex;
          align-items: center;
          min-width: 120px;
          max-width: 200px;
          padding: 0.375rem 0.75rem;
          background: #ffffff;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 0.875rem;
          color: #374151;
          cursor: pointer;
          transition: all 0.2s ease;

          &:hover {
            background: #f9fafb;
            border-color: #9ca3af;
          }

          &:focus {
            outline: none;
            border-color: var(--primary-community, #3b82f6);
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          }
        }

        .compact-tags {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          flex-wrap: wrap;

          .compact-filter-tag {
            background: #f9fafb;
            border: 1px solid #e5e7eb;
            border-radius: 12px;
            padding: 0.25rem 0.5rem;
            font-size: 0.75rem;
            color: #6b7280;
            cursor: pointer;
            transition: all 0.2s ease;
            display: inline-flex;
            align-items: center;
            white-space: nowrap;

            &:hover {
              background: #f3f4f6;
              border-color: #d1d5db;
            }

            &.enabled {
              background: var(--primary-community, #3b82f6);
              color: white;
              border-color: var(--primary-community, #3b82f6);

              &:hover {
                background: var(--primary-community-hover, #2563eb);
              }
            }
          }
        }
      }

      .filter-actions {
        margin-left: auto;
        display: flex;
        align-items: center;
        gap: 0.5rem;

        .clear-filters-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          background: #f3f4f6;
          color: #6b7280;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s ease;

          &:hover {
            background: #e5e7eb;
            color: #374151;
            border-color: #d1d5db;
          }

          svg {
            width: 14px;
            height: 14px;
          }
        }
      }
    }

    .active-filters-section {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding-top: 0.75rem;
      margin-top: 0.75rem;
      border-top: 1px solid #f3f4f6;

      .active-filters-label {
        font-weight: 500;
        font-size: 0.75rem;
        color: #6b7280;
        white-space: nowrap;
        text-transform: uppercase;
        letter-spacing: 0.025em;
      }

      .active-filters-tags {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        flex: 1;

        .active-filter-tag {
          display: inline-flex;
          align-items: center;
          gap: 0.375rem;
          background: var(--primary-community, #3b82f6);
          color: white;
          border: 1px solid var(--primary-community, #3b82f6);
          border-radius: 16px;
          padding: 0.25rem 0.625rem;
          font-size: 0.75rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;

          &:hover {
            background: var(--primary-community-lighter, #dbeafe);
            border-color: var(--primary-community-lighter, #dbeafe);
            transform: translateY(-1px);
          }

          svg {
            width: 12px;
            height: 12px;
            opacity: 0.8;
          }

          &:hover svg {
            opacity: 1;
          }
        }
      }
    }
  }

  // Responsive design
  @media (max-width: 768px) {
    .modern-filters-container {
      padding: 0.5rem;

      .filters-compact-row {
        flex-direction: column;
        align-items: stretch;
        gap: 0.75rem;

        .filter-group {
          flex-direction: column;
          align-items: stretch;
          gap: 0.25rem;

          &.search-group {
            min-width: auto;
          }

          .filter-label {
            font-size: 0.7rem;
          }

          .compact-dropdown {
            min-width: auto;
            max-width: none;
          }

          .compact-tags {
            justify-content: flex-start;
          }
        }

        .filter-actions {
          margin-left: 0;
          align-self: flex-end;
        }
      }
    }
  }
</style>
